import * as React from 'react'
import { useSnackbar } from 'notistack'
import MaterialTableOriginal, {
  Column,
  Icons,
  MaterialTableProps,
} from 'material-table'

// Type assertion to fix React types incompatibility between material-table and react-intl
const MaterialTable: React.FC<MaterialTableProps<any>> =
  MaterialTableOriginal as any
import { ThemeProvider, useTheme } from '@mui/material/styles'
import { AreYouSure, Sidescroll } from 'components'
import { useIntl } from 'react-intl'
import intl from 'localization/components'
import { columns, icons, localization, snackbarText } from './user-table.model'
import { useQueryClient } from 'react-query'
import {
  handleTableAction,
  handleUserData,
  validate,
} from './user-table.controller'
import {
  Box,
  Switch,
  TablePagination,
  Tooltip,
  Typography,
} from '@mui/material'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import { useDeleteOnlyApiUser } from 'services/queries/use-delete-only-api-user'
import { userIssueApiUserToken } from 'services/queries/use-issue-api-user-token'
import { theme as defaultTheme } from 'styles/theme'

interface UserData {
  id: string | number
  email: string
  role?: string
  name?: string
  phone_number?: string
}

interface ApiUserData {
  id: string | number
  last_used?: string | null
  active?: boolean
}

interface User {
  userData: UserData | null
  apiUserData?: ApiUserData
}

interface CustomerData {
  id: string | number
}

interface UserTableProps {
  customerData: CustomerData
  users: User[]
  maxUsers?: number
  updateUser: (data: {
    userId: string
    data: Partial<UserData>
  }) => Promise<void>
  createNewUser: (data: unknown) => Promise<void>
  removeUser: (userId: string) => Promise<void>
  showTitle?: boolean
  isExpired?: boolean
}

const UserTable = ({
  customerData,
  users,
  maxUsers,
  updateUser,
  createNewUser,
  removeUser,
  showTitle = true,
  isExpired,
}: UserTableProps) => {
  const currentTheme = useTheme()
  const [areYouSureDeleteApiUser, setAreYouSureDeleteApiUser] =
    React.useState(false)
  const [deletingApiUserId, setDeletingApiUserId] = React.useState<
    string | number | null
  >(null)
  const queryClient = useQueryClient()
  const { mutate: issueApiUserToken } = userIssueApiUserToken()
  const reactIntl = useIntl()
  const [isCreatingTestUser, setIsCreatingTestUser] = React.useState(false)
  const messages = reactIntl.messages
  const { enqueueSnackbar } = useSnackbar()

  const invalidateQuery = () => {
    queryClient.invalidateQueries('getUsers' + customerData.id)
  }

  const onSuccess = async () => {
    enqueueSnackbar('API User deleted successfully', {
      variant: 'success',
      autoHideDuration: 3000,
    })
    invalidateQuery()
  }

  const onError = () => {
    enqueueSnackbar('Delete user failed', {
      variant: 'error',
      autoHideDuration: 3000,
    })
    invalidateQuery()
  }

  const { mutateAsync: deleteOnlyApiUser } = useDeleteOnlyApiUser({
    onSuccess,
    onError,
  })

  const handleDeleteApiUser = () => {
    if (deletingApiUserId) {
      deleteOnlyApiUser(deletingApiUserId)
    }
    setAreYouSureDeleteApiUser(false)
  }

  const dashboardUsers = users?.filter((user) => user.userData !== null)

  return (
    <Sidescroll conditionalySidescroll maxWidth={'819px'}>
      <ThemeProvider theme={currentTheme}>
        <Tooltip
          title="When enabled, new users will be created as test users (not tracked in analytics)"
          placement="right"
          arrow
        >
          <Box
            onClick={() => setIsCreatingTestUser(!isCreatingTestUser)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              mt: 2,
              mb: 1,
              ml: 1,
              borderRadius: 2,
              cursor: 'pointer',
              bgcolor: isCreatingTestUser
                ? 'warning.light'
                : (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.04)'
                      : 'grey.50',
              border: '1px solid',
              borderColor: isCreatingTestUser
                ? 'warning.main'
                : (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.12)'
                      : 'grey.200',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: isCreatingTestUser
                  ? 'warning.light'
                  : (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'grey.100',
              },
            }}
          >
            <ScienceOutlinedIcon
              sx={{
                fontSize: 18,
                color: isCreatingTestUser ? 'warning.main' : 'grey.500',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: isCreatingTestUser ? 'warning.dark' : 'text.secondary',
                userSelect: 'none',
              }}
            >
              Test mode
            </Typography>
            <Switch
              size="small"
              checked={isCreatingTestUser}
              onChange={() => setIsCreatingTestUser(!isCreatingTestUser)}
              onClick={(e) => e.stopPropagation()}
              sx={{
                ml: 0.5,
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'warning.main',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'warning.main',
                },
              }}
            />
          </Box>
        </Tooltip>
        <AreYouSure
          setOpen={setAreYouSureDeleteApiUser}
          onSubmit={() => handleDeleteApiUser()}
          open={areYouSureDeleteApiUser}
          title="Delete Api User"
          content="Are you sure you want to delete this Api User? This will revoke all access to the API and the Dashboard."
        />
        <MaterialTable
          options={
            {
              pageSize: 20,
              addRowPosition: 'first',
              pageSizeOptions: [20, 50, 100],
              cellStyle: {
                fontSize: '14px',
                color: currentTheme.palette.text.primary,
              },
              headerStyle: {
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: currentTheme.palette.text.secondary,
                backgroundColor:
                  currentTheme.palette.mode === 'dark'
                    ? currentTheme.palette.background.default
                    : currentTheme.palette.grey[50],
              },
              rowStyle: {
                backgroundColor: currentTheme.palette.background.paper,
              },
            } as any
          }
          components={{
            Container: (props: any) => <Box {...props} />,
            Pagination: (
              props: React.ComponentProps<typeof TablePagination>,
            ) => (
              <TablePagination {...props} style={{ paddingRight: '20px' }} />
            ),
          }}
          localization={localization(messages)}
          title={
            showTitle ? (messages[intl.settings('all-users')] as string) : ''
          }
          columns={columns(messages) as Column<any>[]}
          icons={icons as unknown as Icons}
          actions={[
            (rowData: User) => {
              return {
                icon: icons.DeleteApi,
                tooltip: rowData?.userData
                  ? 'To delete this user you should use the other delete button'
                  : 'Delete Api User',
                onClick: (_event: React.MouseEvent, data: User | User[]) => {
                  const row = Array.isArray(data) ? data[0] : data
                  setAreYouSureDeleteApiUser(true)
                  return setDeletingApiUserId(row?.apiUserData?.id ?? null)
                },
                disabled: !!rowData?.userData,
              }
            },
            (rowData: User) => {
              return {
                icon: icons.UserApiToken,
                tooltip: rowData?.apiUserData
                  ? 'Issue API token for this user'
                  : 'You can not issue tokens for this user. Try creating a new one',
                onClick: (_event: React.MouseEvent, data: User | User[]) => {
                  const row = Array.isArray(data) ? data[0] : data
                  return issueApiUserToken(String(row?.apiUserData?.id ?? ''))
                },
                disabled: !rowData?.apiUserData,
              }
            },
          ]}
          data={users}
          editable={{
            isDeletable: (rowData: User) => !!rowData?.userData,
            isEditable: (rowData: User) => !!rowData?.userData,
            onRowAdd: ({ userData: newUserData }: { userData: UserData }) => {
              return handleTableAction({
                action: () => {
                  const _hsq = ((
                    window as unknown as { _hsq: unknown[] }
                  )._hsq =
                    (window as unknown as { _hsq: unknown[] })._hsq || [])
                  if (!isCreatingTestUser) {
                    _hsq.push([
                      'identify',
                      {
                        email: newUserData.email,
                      },
                    ])
                  }
                  _hsq.push(['trackPageView'])
                  return createNewUser(
                    handleUserData(newUserData, customerData),
                  )
                },
                successText: () =>
                  enqueueSnackbar(snackbarText('create', true), {
                    variant: 'success',
                    autoHideDuration: 3000,
                  }),
                illegalFunctionText: () =>
                  enqueueSnackbar(
                    isExpired
                      ? "You can't add users to an expired company."
                      : "You can't add more users to this company. Please go to restrictions tab and update the max user limit.",
                    {
                      variant: 'error',
                      autoHideDuration: 3000,
                    },
                  ),
                failureText: () =>
                  enqueueSnackbar(snackbarText('create', false), {
                    variant: 'error',
                    autoHideDuration: 3000,
                  }),
                invalidateQuery,
                isFunctionLegal: !isExpired
                  ? maxUsers !== undefined && dashboardUsers.length < maxUsers
                  : false,
                validation: validate(newUserData),
              })
            },
            onRowUpdate: (newUserData: User) => {
              return handleTableAction({
                action: () =>
                  updateUser({
                    userId: String(newUserData.userData!.id),
                    data: {
                      role: newUserData.userData!.role,
                      name: newUserData.userData!.name,
                      phone_number: newUserData.userData?.phone_number ?? '',
                    },
                  }),
                successText: () =>
                  enqueueSnackbar(snackbarText('update', true), {
                    variant: 'success',
                    autoHideDuration: 3000,
                  }),
                failureText: () => {
                  enqueueSnackbar(snackbarText('update', false), {
                    variant: 'error',
                    autoHideDuration: 3000,
                  })
                },
                invalidateQuery,
              })
            },
            onRowDelete: (oldData: User) =>
              handleTableAction({
                action: () => removeUser(String(oldData.userData!.id)),
                successText: () =>
                  enqueueSnackbar(snackbarText('delete', true), {
                    variant: 'success',
                    autoHideDuration: 3000,
                  }),
                failureText: () =>
                  enqueueSnackbar(snackbarText('delete', false), {
                    variant: 'error',
                    autoHideDuration: 3000,
                  }),
                invalidateQuery,
              }),
          }}
        />
      </ThemeProvider>
    </Sidescroll>
  )
}

export default UserTable
